import moment from 'moment'
import 'moment/locale/sk'
import 'moment/locale/cs'
import 'moment/locale/ru'
import 'moment/locale/uk'
import 'moment/locale/de'
import i18n from 'i18next'
import Backend from 'i18next-xhr-backend'
import { initReactI18next } from 'react-i18next'
import packageJson from '../../package.json'
import { normalizeUiLanguage, detectBrowserUiLanguage } from '@services/helpers/languages'
import { LANGUAGE_CODES } from '@services/helpers/settings'

function mapLngToMoment(lng) {
    return normalizeUiLanguage(lng, 'en')
}

function syncMomentLocale(lng) {
    moment.locale(mapLngToMoment(lng))
}

function getPersistedLanguage() {
    try {
        const raw = localStorage.getItem('persist:root')
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (parsed.language === undefined) return null
        const language = JSON.parse(parsed.language)
        return normalizeUiLanguage(language, '')
    } catch (error) {
        return null
    }
}

export function isBootPath(path = '') {
    return (
        path === '/' ||
        path.startsWith('/login') ||
        path.startsWith('/password-reset')
    )
}

export function isTerminalPath(path = '') {
    return path.startsWith('/attendance') || path.startsWith('/warehousing')
}

export function resolveNamespaceForPath(path = '') {
    if (isTerminalPath(path)) return 'terminal'
    if (isBootPath(path)) return 'boot'
    return 'translations'
}

export function resolveNamespaceForRole(role, canAccessWarehouse = false) {
    if (parseInt(role) === 5) return 'terminal'
    return 'translations'
}

export async function ensureI18nNamespace(namespace) {
    if (!namespace) return

    const alreadyReady =
        i18n.options.defaultNS === namespace && i18n.hasResourceBundle(i18n.language, namespace)
    if (alreadyReady) return

    if (namespace === 'translations') {
        i18n.options.fallbackLng = 'en'
    } else {
        i18n.options.fallbackLng = false
    }

    if (!i18n.hasResourceBundle(i18n.language, namespace)) {
        await i18n.loadNamespaces(namespace)
    }

    if (i18n.options.defaultNS !== namespace) {
        i18n.setDefaultNamespace(namespace)
    }

    if (!i18n.options.ns) {
        i18n.options.ns = [namespace]
    } else if (!i18n.options.ns.includes(namespace)) {
        i18n.options.ns = [...i18n.options.ns, namespace]
    }
}

const initialLanguage = getPersistedLanguage() || detectBrowserUiLanguage('en') || 'en'
const initialPath = typeof window !== 'undefined' ? window.location.pathname || '/' : '/'
const activeNamespace = resolveNamespaceForPath(initialPath)
const buildVersion = packageJson.buildDate || Date.now()

const originalChangeLanguage = i18n.changeLanguage.bind(i18n)
i18n.changeLanguage = (lng, ...rest) => {
    const normalized = lng ? normalizeUiLanguage(lng, lng) : lng
    return originalChangeLanguage(normalized, ...rest)
}

i18n.use(Backend)
    .use(initReactI18next)
    .init(
        {
            lng: initialLanguage,
            backend: {
                loadPath: `/assets/i18n/{{ns}}/{{lng}}.json?v=${buildVersion}`,
            },
            fallbackLng: activeNamespace === 'translations' ? 'en' : false,
            load: 'currentOnly',
            supportedLngs: LANGUAGE_CODES,
            nonExplicitSupportedLngs: true,
            debug: false,
            ns: [activeNamespace],
            defaultNS: activeNamespace,
            interpolation: {
                escapeValue: false,
            },
            react: {
                wait: true,
            },
        },
        () => {
            syncMomentLocale(i18n.language)
        },
    )

export const ESIGN_SUPPORTED_LANGUAGES = LANGUAGE_CODES

i18n.on('languageChanged', (lng) => {
    syncMomentLocale(lng)
})

export default i18n
